/**
 * mobile-account-drawer.tsx — Ngăn "Cá nhân" của bản điện thoại, XỔ NGANG từ cạnh trái (kiểu ngăn hồ sơ của Lark Mobile), mở khi bấm
 * ảnh đại diện + tên ở đầu màn Nhắn tin (mobile-inbox-header.tsx):
 *   đầu ngăn: ảnh lớn (chấm trạng thái) + nút Trạng thái (Trực tuyến / Bận / Ngoại tuyến) + tên (bấm → Hồ sơ) + tên tổ chức · vai trò;
 *   các mục có biểu tượng màu: Hồ sơ, Gói dịch vụ, Bắt đầu (khi còn việc), Tải ứng dụng (khi cài được), Cài đặt → màn Cài đặt dạng thẻ nhóm
 *   (pages/account/account-mobile-settings.tsx, có Đăng xuất).
 */
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Download, Gem, Rocket, Settings, UserRound, type LucideIcon } from 'lucide-react'
import { SideDrawer, SideDrawerContent } from '@/components/ui/side-drawer'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ONBOARDING_NAV, sectionLabel } from './nav-config'
import { useOnboardingEntry } from './onboarding-entry'
import { useInstallApp } from './install-app'
import { useAuthStore } from '@/stores/auth-store'
import { usePresenceMap, usePresenceRealtime, useSetMyPresence } from '@/hooks/use-presence'
import { PRESENCE_DOT, PRESENCE_LABEL, PRESENCE_OPTIONS, isPresenceStatus, presenceOf } from '@/lib/presence'
import { apiError } from '@/lib/api-client'
import { resolvePlan } from '@/pages/billing/plans'
import { usePendingOrder } from '@/pages/billing/pending-order-bar'
import { ROLE_LABELS, avatarSrc } from '@/pages/settings/settings-utils'
import { cn, initials } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export function MobileAccountDrawer({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const user = useAuthStore((s) => s.user)
  const { t } = useTranslation('shell')
  const onboarding = useOnboardingEntry()
  const installApp = useInstallApp()
  const { pending, lapsed } = usePendingOrder()
  // Trạng thái trực tuyến: chỉ tải / nghe socket khi ngăn đang mở.
  usePresenceRealtime(open ? user?.id : undefined)
  const presenceMap = usePresenceMap(open && !!user)
  const setPresence = useSetMyPresence()
  if (!user) return null

  const close = () => onOpenChange(false)
  const avatar = avatarSrc(user.avatarUrl)
  const status = presenceOf(presenceMap.data, user.id)
  // Phiên "xem dưới quyền" không đổi trạng thái của nhân viên đó.
  const impersonating = !!user.impersonatedBy
  const changeStatus = (v: string) => {
    if (!isPresenceStatus(v) || v === status) return
    setPresence.mutate(v, {
      onSuccess: () => toast.success(t('userMenu.statusChanged', { status: PRESENCE_LABEL[v] })),
      onError: (err) => toast.error(apiError(err)),
    })
  }

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange}>
      <SideDrawerContent title={t('drawer.title')} className="w-[82vw] max-w-sm">
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="px-5 pb-4 pt-5">
            <div className="flex items-start justify-between gap-3">
              <span className="relative shrink-0">
                <Avatar className="h-[72px] w-[72px]">
                  {avatar && <AvatarImage src={avatar} />}
                  <AvatarFallback className="text-xl">{initials(user.fullName)}</AvatarFallback>
                </Avatar>
                <span className={cn('absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-background', PRESENCE_DOT[status])} aria-hidden />
              </span>

              <DropdownMenu>
                <DropdownMenuTrigger
                  disabled={impersonating}
                  aria-label={`${t('userMenu.status')}: ${PRESENCE_LABEL[status]}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-primary px-3 text-sm font-medium text-primary outline-none active:bg-primary/10 disabled:opacity-50"
                >
                  <span className={cn('h-2 w-2 rounded-full', PRESENCE_DOT[status])} />
                  {PRESENCE_LABEL[status]}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuRadioGroup value={status} onValueChange={changeStatus}>
                    {PRESENCE_OPTIONS.map((o) => (
                      <DropdownMenuRadioItem key={o.id} value={o.id} disabled={setPresence.isPending}>
                        <span className={cn('mr-2 mt-1.5 h-2 w-2 shrink-0 self-start rounded-full', PRESENCE_DOT[o.id])} />
                        <span className="min-w-0">
                          <span className="block">{o.label}</span>
                          <span className="block text-xs text-muted-foreground">{o.hint}</span>
                        </span>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Link to="/account/profile" onClick={close} className="mt-3 flex items-center gap-1 active:opacity-70">
              <span className="min-w-0 truncate text-2xl font-semibold leading-tight">{user.fullName}</span>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </Link>
            {user.org?.name && <p className="mt-1 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{user.org.name}</p>}
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{user.roleName || ROLE_LABELS[user.role] || user.role}</p>
          </div>

          <nav aria-label={t('drawer.title')} className="pb-4">
            <DrawerRow to="/account/profile" icon={UserRound} tone="text-primary" label={t('drawer.profile')} onClick={close} />
            <DrawerRow
              to="/billing"
              icon={Gem}
              tone="text-amber-500"
              label={t('rail.plan')}
              value={pending ? t('drawer.orderPending') : lapsed ? t('drawer.orderExpired') : resolvePlan(user.org?.plan).name}
              onClick={close}
            />
            {onboarding.show && (
              <DrawerRow
                to={ONBOARDING_NAV.to}
                icon={Rocket}
                tone="text-primary"
                label={sectionLabel(ONBOARDING_NAV)}
                value={`${onboarding.steps.doneCount}/${onboarding.steps.total}`}
                onClick={close}
              />
            )}
            {installApp.visible && (
              <button
                type="button"
                onClick={() => {
                  close()
                  void installApp.run()
                }}
                className="flex w-full items-center gap-4 px-5 py-4 text-left active:bg-muted"
              >
                <Download className="h-6 w-6 shrink-0 text-emerald-500" />
                <span className="flex-1 text-[17px]">{t('userMenu.installApp')}</span>
              </button>
            )}
            <DrawerRow to="/account" icon={Settings} tone="text-primary" label={t('drawer.settings')} onClick={close} />
          </nav>
        </div>
      </SideDrawerContent>
    </SideDrawer>
  )
}

function DrawerRow({
  to, icon: Icon, tone, label, value, onClick,
}: { to: string; icon: LucideIcon; tone: string; label: string; value?: string; onClick: () => void }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-4 px-5 py-4 active:bg-muted">
      <Icon className={cn('h-6 w-6 shrink-0', tone)} />
      <span className="min-w-0 flex-1 truncate text-[17px]">{label}</span>
      {value && <span className="max-w-32 shrink-0 truncate rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">{value}</span>}
    </Link>
  )
}
